import React, { useState, useCallback, useMemo } from 'react';

type LaunchConfig = {
  id: string;
  name: string;
  type: 'dev' | 'build' | 'test' | 'lint' | 'custom';
  command?: string;
  env?: Record<string, string>;
  preLaunchTask?: string;
  default?: boolean;
};

type LaunchConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: Record<string, string>;
  onUpdateLaunchConfig: (content: string) => void;
  onRunConfig: (config: LaunchConfig) => void;
};

const CONFIG_FILE = '.atlas-ide/launch.json';

const defaultConfigs: LaunchConfig[] = [
  {
    id: 'dev',
    name: 'Development Server',
    type: 'dev',
    command: 'npm run start',
    default: true,
  },
  {
    id: 'build',
    name: 'Production Build',
    type: 'build',
    command: 'npm run build',
  },
  {
    id: 'test',
    name: 'Run Tests',
    type: 'test',
    command: 'npm test',
  },
  {
    id: 'lint',
    name: 'Lint Code',
    type: 'lint',
    command: 'npm run lint',
  },
];

export const LaunchConfigModal = React.memo(function LaunchConfigModal({
  isOpen,
  onClose,
  files,
  onUpdateLaunchConfig,
  onRunConfig,
}: LaunchConfigModalProps) {
  const [editingConfig, setEditingConfig] = useState<LaunchConfig | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Parse launch configurations
  const configs = useMemo<LaunchConfig[]>(() => {
    const content = files[CONFIG_FILE] || files[`/${CONFIG_FILE}`];
    if (!content) return defaultConfigs;
    try {
      const parsed = JSON.parse(content);
      if (parsed.configurations && Array.isArray(parsed.configurations)) {
        return parsed.configurations;
      }
      return defaultConfigs;
    } catch {
      return defaultConfigs;
    }
  }, [files]);

  const saveConfigs = useCallback((newConfigs: LaunchConfig[]) => {
    const content = JSON.stringify({ version: '1.0', configurations: newConfigs }, null, 2);
    onUpdateLaunchConfig(content);
  }, [onUpdateLaunchConfig]);

  const handleAddConfig = useCallback(() => {
    setEditingConfig({
      id: `config-${Date.now()}`,
      name: '',
      type: 'custom',
      command: '',
    });
    setShowForm(true);
  }, []);

  const handleEditConfig = useCallback((config: LaunchConfig) => {
    setEditingConfig({ ...config });
    setShowForm(true);
  }, []);

  const handleDeleteConfig = useCallback((configId: string) => {
    const newConfigs = configs.filter(c => c.id !== configId);
    saveConfigs(newConfigs);
  }, [configs, saveConfigs]);

  const handleSaveConfig = useCallback(() => {
    if (!editingConfig || !editingConfig.name.trim()) return;

    const existingIndex = configs.findIndex(c => c.id === editingConfig.id);
    let newConfigs: LaunchConfig[];

    if (existingIndex >= 0) {
      newConfigs = [...configs];
      newConfigs[existingIndex] = editingConfig;
    } else {
      newConfigs = [...configs, editingConfig];
    }

    saveConfigs(newConfigs);
    setEditingConfig(null);
    setShowForm(false);
  }, [editingConfig, configs, saveConfigs]);

  const handleCancelEdit = useCallback(() => {
    setEditingConfig(null);
    setShowForm(false);
  }, []);

  const handleSetDefault = useCallback((configId: string) => {
    const newConfigs = configs.map(c => ({
      ...c,
      default: c.id === configId,
    }));
    saveConfigs(newConfigs);
  }, [configs, saveConfigs]);

  const handleRunConfig = useCallback((config: LaunchConfig) => {
    onRunConfig(config);
    onClose();
  }, [onRunConfig, onClose]);

  const handleClose = useCallback(() => {
    setEditingConfig(null);
    setShowForm(false);
    onClose();
  }, [onClose]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'dev': return 'D';
      case 'build': return 'B';
      case 'test': return 'T';
      case 'lint': return 'L';
      default: return 'C';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'dev': return 'var(--success)';
      case 'build': return 'var(--accent)';
      case 'test': return 'var(--warning)';
      case 'lint': return 'var(--error)';
      default: return 'var(--muted)';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '550px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div className="modal-title">
            {showForm ? (
              <button
                onClick={handleCancelEdit}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  padding: '0 8px 0 0',
                  fontSize: '14px',
                }}
              >
                &larr;
              </button>
            ) : null}
            {showForm ? (editingConfig?.id.startsWith('config-') ? 'New Configuration' : 'Edit Configuration') : 'Launch Configurations'}
          </div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {!showForm ? (
            <>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ color: 'var(--muted)', fontSize: '12px', margin: 0 }}>
                  Define run and debug configurations for your project.
                </p>
                <button
                  className="pill-btn"
                  onClick={handleAddConfig}
                  style={{ background: 'var(--accent)', color: 'white', fontSize: '12px' }}
                >
                  + Add
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {configs.map((config) => (
                  <div
                    key={config.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      border: config.default ? '1px solid var(--accent)' : '1px solid var(--border)',
                    }}
                  >
                    <span
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: getTypeColor(config.type),
                        color: 'white',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        fontSize: '14px',
                      }}
                    >
                      {getTypeIcon(config.type)}
                    </span>

                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '13px', color: 'var(--text)' }}>
                          {config.name}
                        </span>
                        {config.default && (
                          <span style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            background: 'var(--accent)',
                            color: 'white',
                            borderRadius: '4px',
                          }}>
                            Default
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                        {config.command || 'No command specified'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        className="pill-btn"
                        onClick={() => handleRunConfig(config)}
                        style={{
                          background: 'var(--success)',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px 10px',
                        }}
                      >
                        Run
                      </button>
                      <button
                        className="pill-btn"
                        onClick={() => handleEditConfig(config)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Edit
                      </button>
                      {!config.default && (
                        <button
                          className="pill-btn"
                          onClick={() => handleSetDefault(config.id)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          title="Set as default"
                        >
                          *
                        </button>
                      )}
                      <button
                        className="pill-btn"
                        onClick={() => handleDeleteConfig(config.id)}
                        style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                      >
                        x
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {configs.length === 0 && (
                <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
                  No configurations yet. Click "+ Add" to create one.
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Name:
                </label>
                <input
                  type="text"
                  value={editingConfig?.name || ''}
                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, name: e.target.value } : null)}
                  placeholder="e.g., Development Server"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Type:
                </label>
                <select
                  value={editingConfig?.type || 'custom'}
                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, type: e.target.value as LaunchConfig['type'] } : null)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                >
                  <option value="dev">Development</option>
                  <option value="build">Build</option>
                  <option value="test">Test</option>
                  <option value="lint">Lint</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Command:
                </label>
                <input
                  type="text"
                  value={editingConfig?.command || ''}
                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, command: e.target.value } : null)}
                  placeholder="e.g., npm run start"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Pre-launch Task (optional):
                </label>
                <input
                  type="text"
                  value={editingConfig?.preLaunchTask || ''}
                  onChange={(e) => setEditingConfig(prev => prev ? { ...prev, preLaunchTask: e.target.value } : null)}
                  placeholder="e.g., npm install"
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                  }}
                />
              </div>

              <div style={{
                padding: '12px',
                background: 'var(--bg-secondary)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'var(--muted)',
              }}>
                <strong>Tip:</strong> Configurations are saved to <code style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: '2px' }}>.atlas-ide/launch.json</code>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {showForm ? (
            <>
              <button className="pill-btn" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button
                className="pill-btn"
                onClick={handleSaveConfig}
                disabled={!editingConfig?.name.trim()}
                style={{
                  background: 'var(--accent)',
                  color: 'white',
                  opacity: editingConfig?.name.trim() ? 1 : 0.5,
                }}
              >
                Save
              </button>
            </>
          ) : (
            <button className="pill-btn" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default LaunchConfigModal;
